import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { CalendarClock } from "lucide-react";

const typeLabel = (t) => t.charAt(0).toUpperCase() + t.slice(1);

const fmtLong = (iso) =>
  new Date(iso).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

const fmtShort = (iso) =>
  new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });

const CARD_STYLE = { backgroundColor: "#112A57", borderColor: "#1E3A8A" };

function NeverOpenedCard({ cat }) {
  return (
    <div data-testid={`window-card-${cat.category}-never`} className="rounded-xl border p-4 opacity-70" style={CARD_STYLE}>
      <p className="text-white font-heading font-semibold text-sm">{cat.label}</p>
      <p className="text-xs text-[#9CA3AF] mt-2" data-testid={`window-card-status-${cat.category}-never`}>
        Review window currently closed. No items to review.
      </p>
    </div>
  );
}

function WindowCard({ w }) {
  const tid = `${w.category}-${w.type}`;
  const label = typeLabel(w.type);

  if (w.state === "open" && w.indefinite) {
    return (
      <div data-testid={`window-card-${tid}`} className="rounded-xl border p-4" style={CARD_STYLE}>
        <p className="text-white font-heading font-semibold text-sm">{w.label}</p>
        <p className="text-sm mt-1.5 font-medium" style={{ color: "#10B981" }} data-testid={`window-card-status-${tid}`}>
          {label} window open.
        </p>
      </div>
    );
  }

  if (w.state === "open") {
    const pct = Math.max(0, Math.min(100, (w.remaining_business_days / w.duration_days) * 100));
    const color = w.within_final_2 ? "#F59E0B" : "#10B981";
    return (
      <div data-testid={`window-card-${tid}`} className="rounded-xl border p-4" style={CARD_STYLE}>
        <div className="flex items-center justify-between gap-2">
          <p className="text-white font-heading font-semibold text-sm">{w.label}</p>
          <span className="text-sm font-medium" style={{ color }} data-testid={`window-card-status-${tid}`}>
            {label} window open.
          </span>
        </div>
        <div className="mt-2.5 h-2 rounded-full overflow-hidden" style={{ backgroundColor: "#0A1F44" }}>
          <div data-testid={`window-card-bar-${tid}`} className="h-full rounded-full transition-all"
            style={{ width: `${pct}%`, backgroundColor: color }} />
        </div>
        <p className="text-xs text-[#9CA3AF] mt-2" data-testid={`window-card-remaining-${tid}`}>
          {w.remaining_business_days} business day{w.remaining_business_days === 1 ? "" : "s"} remaining. Closes 5pm {fmtLong(w.closes_at)}.
        </p>
        {w.include_changes && (
          <p className="text-xs mt-1" style={{ color: "#FDBA74" }} data-testid={`window-card-included-${tid}`}>
            Changes requested inside this window are included.
          </p>
        )}
      </div>
    );
  }

  const closedDate = w.closed_at || w.closes_at;
  return (
    <div data-testid={`window-card-${tid}`} className="rounded-xl border p-4 opacity-70" style={CARD_STYLE}>
      <p className="text-white font-heading font-semibold text-sm">{w.label}</p>
      <div className="mt-2.5 h-2 rounded-full" style={{ backgroundColor: "#374151" }} data-testid={`window-card-bar-${tid}`} />
      <p className="text-xs text-[#9CA3AF] mt-2" data-testid={`window-card-status-${tid}`}>
        {label} window closed{closedDate ? ` ${fmtShort(closedDate)}` : ""}. New requests are quoted before work starts.
      </p>
    </div>
  );
}

export function ReviewWindowCards({ clientId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/clients/${clientId}/windows`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [clientId]);

  if (!data) return null;
  const latest = {};
  data.windows.forEach((w) => { latest[`${w.category}|${w.type}`] = w; });
  const cards = Object.values(latest);
  const covered = new Set(cards.map((w) => w.category));
  const neverOpened = data.categories.filter((c) => c.doc_count > 0 && !covered.has(c.category));
  if (!cards.length && !neverOpened.length) return null;

  return (
    <div className="mb-8" data-testid="review-window-cards">
      <p className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF] mb-3 flex items-center gap-1.5">
        <CalendarClock size={13} style={{ color: "#F97316" }} /> Windows
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        {cards.map((w) => <WindowCard key={w.id} w={w} />)}
        {neverOpened.map((c) => <NeverOpenedCard key={c.category} cat={c} />)}
      </div>
    </div>
  );
}
